// Пилюли (components/Badge/README.md): статус, «ключ: значение», чип фильтра, пилюля свойства.
import { X } from 'lucide-react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from '../lib/cx.ts';
import { Icon } from '../lib/icon.tsx';
import type { LucideIcon } from '../lib/icon.tsx';
import { IconButton } from './IconButton.tsx';

/** neutral — черновик; accent — завершён; warn — возвращён; ok — подписан; bad — проблема. */
export type BadgeTone = 'neutral' | 'accent' | 'ok' | 'warn' | 'bad';

export interface BadgeProps {
  tone?: BadgeTone;
  /** Слово статуса — обязательно: цвет не единственный носитель смысла. */
  children: ReactNode;
  icon?: LucideIcon;
  /** Точка цвета статуса перед словом. */
  dot?: boolean;
  className?: string;
}

export function Badge({ tone = 'neutral', children, icon, dot = false, className }: BadgeProps) {
  return (
    <span className={cx('t-badge', tone !== 'neutral' && tone, className)}>
      {dot ? <span className="t-dot" aria-hidden="true" /> : null}
      {icon ? <Icon icon={icon} /> : null}
      {children}
    </span>
  );
}

/** Пилюля «ключ: значение» под заголовком страницы. */
export function MetaChip({
  label,
  children,
  icon,
}: {
  label: ReactNode;
  children: ReactNode;
  icon?: LucideIcon;
}) {
  return (
    <span className="t-meta">
      {icon ? <Icon icon={icon} /> : null}
      {label}: {children}
    </span>
  );
}

export interface FilterChipProps {
  /** Условие: «Роль». */
  field: ReactNode;
  /** Значения: «контролёр, ведущий». */
  children: ReactNode;
  /** Клик по чипу меняет значения. */
  onEdit?: () => void;
  /** ✕ убирает условие. */
  onRemove: () => void;
  /** Подпись ✕ для программ чтения экрана. */
  removeLabel: string;
}

/** Чип активного условия фильтра фразой: «Роль — контролёр». */
export function FilterChip({ field, children, onEdit, onRemove, removeLabel }: FilterChipProps) {
  return (
    <span className="t-chip">
      {onEdit ? (
        <button type="button" className="kp-chip-edit" onClick={onEdit}>
          <b>{field}</b> — {children}
        </button>
      ) : (
        <span>
          <b>{field}</b> — {children}
        </span>
      )}
      <IconButton icon={X} label={removeLabel} size="sm" onClick={onRemove} />
    </span>
  );
}

export interface PropPillProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: LucideIcon;
  /** Пустое значение приглашает к действию приглушённым текстом. */
  empty?: boolean;
}

/** Пилюля свойства: один и тот же выбор везде (ux.md, раздел 5). */
export function PropPill({
  icon,
  empty,
  className,
  children,
  type = 'button',
  ...rest
}: PropPillProps) {
  return (
    <button type={type} className={cx('t-prop', empty && 'empty', className)} {...rest}>
      {icon ? <Icon icon={icon} /> : null}
      {children}
    </button>
  );
}
